import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AdminPanelSettings as AdminIcon, People as ContactsIcon, Email as EmailIcon, Menu as MenuIcon, Search as SearchIcon, Sync as SyncIcon, Insights as UsageAdminIcon,
// TargetIcon belongs to the disabled Target Markets nav item — restore with it.
// TrackChanges as TargetIcon,
 } from "@mui/icons-material";
import { AppBar, Avatar, alpha, Badge, Box, Chip, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Tab, Tabs, Toolbar, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useOpenLeadReplyCount } from "../../hooks/useOpenLeadReplyCount";
import { useUsage } from "../../hooks/useUsage";
const Header = () => {
    var _a;
    const { user, logout } = useAuth();
    const { usage } = useUsage({ enabled: !!user });
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
    const theme = useTheme();
    // Below md the tabs don't fit next to the credits and avatar, so they move into a drawer
    const isCompactNav = useMediaQuery(theme.breakpoints.down("md"));
    const navBackgroundColor = theme.palette.mode === "dark" ? "#1a1d24" : "#33475b";
    const location = useLocation();
    // Check if user is internal/admin
    const isInternal = (user === null || user === void 0 ? void 0 : user.type) === "internal";
    const openLeadReplyCount = useOpenLeadReplyCount({ enabled: isInternal });
    // Build navigation items
    const navItems = [
        { label: "Search", icon: _jsx(SearchIcon, {}), path: "/search" },
        { label: "Contacts", icon: _jsx(ContactsIcon, {}), path: "/contacts" },
        { label: "Data & Integrations", icon: _jsx(SyncIcon, {}), path: "/data-integrations" },
    ];
    // Add Admin tabs for internal users
    // Target Markets is disabled: its scrape endpoints throw "Apollo scraping has been
    // removed", so the feature cannot do the one thing it exists for. Route and components
    // are intact — see docs/feature-disabling-audit.md.
    if (isInternal) {
        // navItems.push({ label: "Target Markets", icon: <TargetIcon />, path: "/target-markets" });
        navItems.push({ label: "Admin Review", icon: _jsx(AdminIcon, {}), path: "/admin" });
        navItems.push({
            label: "Deal Follow-ups",
            icon: (_jsx(Badge, Object.assign({ badgeContent: openLeadReplyCount, color: "warning" }, { children: _jsx(EmailIcon, {}) }))),
            path: "/admin/deals",
        });
        navItems.push({ label: "Plans & Usage", icon: _jsx(UsageAdminIcon, {}), path: "/admin/usage" });
    }
    // Get current tab index based on path
    const getCurrentTab = () => {
        const currentPath = location.pathname;
        // First check for exact match
        const exactIndex = navItems.findIndex((item) => item.path === currentPath);
        if (exactIndex >= 0)
            return exactIndex;
        // Then find the longest matching path prefix (more specific routes first)
        let bestMatch = -1;
        let bestMatchLength = 0;
        navItems.forEach((item, index) => {
            if (currentPath.startsWith(item.path) && item.path.length > bestMatchLength) {
                bestMatch = index;
                bestMatchLength = item.path.length;
            }
        });
        return bestMatch >= 0 ? bestMatch : 0;
    };
    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleLogout = () => {
        handleClose();
        logout({ logoutParams: { returnTo: window.location.origin } });
    };
    return (_jsxs(AppBar, Object.assign({ position: "static", elevation: 0, sx: {
            backgroundColor: navBackgroundColor,
            borderBottom: `1px solid ${alpha("#000", 0.12)}`,
            color: "#fff",
        } }, { children: [_jsxs(Toolbar, Object.assign({ sx: { minHeight: "64px !important", gap: { xs: 1, md: 2 }, px: { xs: 1.5, sm: 2 } } }, { children: [isCompactNav && (_jsx(IconButton, Object.assign({ onClick: () => setIsNavDrawerOpen(true), "aria-label": "Open navigation", sx: { color: "#fff" } }, { children: _jsx(Badge, Object.assign({ badgeContent: openLeadReplyCount, color: "warning" }, { children: _jsx(MenuIcon, {}) })) }))), _jsx(Box, Object.assign({ sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mr: { xs: "auto", md: 3 },
                            minWidth: 0,
                        } }, { children: _jsx(Typography, Object.assign({ variant: "h6", sx: {
                                fontWeight: 700,
                                fontSize: { xs: "16px", sm: "18px" },
                                color: "#fff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            } }, { children: "BringOnBoard" })) })), !isCompactNav && (_jsx(Tabs, Object.assign({ value: getCurrentTab(), variant: "scrollable", scrollButtons: "auto", sx: {
                            flex: 1,
                            minHeight: 64,
                            "& .MuiTabs-indicator": {
                                backgroundColor: "#FF004E",
                                height: 3,
                            },
                            "& .MuiTabs-flexContainer": {
                                height: "100%",
                            },
                        } }, { children: navItems.map((item) => (_jsx(Tab, { component: Link, to: item.path, icon: item.icon, iconPosition: "start", label: item.label, sx: {
                                minHeight: 64,
                                color: alpha("#fff", 0.7),
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                gap: 0.75,
                                px: 2,
                                "&.Mui-selected": {
                                    color: "#fff",
                                    fontWeight: 600,
                                },
                                "&:hover": {
                                    color: "#fff",
                                    backgroundColor: alpha("#fff", 0.08),
                                },
                                "& .MuiSvgIcon-root": {
                                    fontSize: 20,
                                },
                            } }, item.path))) }))), _jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexShrink: 0 } }, { children: [usage && (_jsx(Chip, { onClick: () => navigate("/usage"), label: _jsxs(Box, Object.assign({ component: "span", sx: { display: "flex", alignItems: "center", gap: 0.5 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", sx: { fontSize: "13px", color: "inherit", display: { xs: "none", sm: "inline" } } }, { children: "Credits:" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { fontWeight: 700, fontSize: "13px", color: "inherit" } }, { children: usage.isUnlimited ? "∞" : usage.credits.total.toLocaleString() }))] })), size: "small", sx: {
                                    height: 28,
                                    backgroundColor: alpha("#4caf50", 0.2),
                                    color: "#8bc34a",
                                    borderRadius: "6px",
                                    fontWeight: 600,
                                    border: `1px solid ${alpha("#4caf50", 0.3)}`,
                                } })), _jsx(IconButton, Object.assign({ onClick: handleMenu, size: "small" }, { children: _jsx(Avatar, Object.assign({ sx: {
                                        width: 34,
                                        height: 34,
                                        backgroundColor: "#FF004E",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                    } }, { children: ((_a = user === null || user === void 0 ? void 0 : user.name) === null || _a === void 0 ? void 0 : _a.charAt(0).toUpperCase()) || "U" })) })), _jsxs(Menu, Object.assign({ anchorEl: anchorEl, open: Boolean(anchorEl), onClose: handleClose, transformOrigin: { horizontal: "right", vertical: "top" }, anchorOrigin: { horizontal: "right", vertical: "bottom" }, PaperProps: {
                                    sx: {
                                        mt: 1,
                                        minWidth: 200,
                                        borderRadius: "8px",
                                    },
                                } }, { children: [_jsx(Box, Object.assign({ sx: { px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` } }, { children: _jsx(Typography, Object.assign({ variant: "subtitle2", sx: { fontWeight: 600 } }, { children: user === null || user === void 0 ? void 0 : user.email })) })), _jsx(MenuItem, Object.assign({ onClick: () => {
                                            handleClose();
                                            navigate("/usage");
                                        }, sx: { py: 1 } }, { children: "Usage & billing" })), _jsx(MenuItem, Object.assign({ onClick: handleLogout, sx: { py: 1, color: "error.main" } }, { children: "Logout" }))] }))] }))] })), _jsxs(Drawer, Object.assign({ open: isCompactNav && isNavDrawerOpen, onClose: () => setIsNavDrawerOpen(false), PaperProps: { sx: { width: 260, maxWidth: "80vw", backgroundColor: navBackgroundColor, color: "#fff" } } }, { children: [_jsx(Typography, Object.assign({ sx: { px: 2.5, py: 2.5, fontWeight: 700, fontSize: "18px" } }, { children: "BringOnBoard" })), _jsx(List, Object.assign({ disablePadding: true }, { children: navItems.map((item, index) => {
                            const isActive = index === getCurrentTab();
                            return (_jsxs(ListItemButton, Object.assign({ component: Link, to: item.path, onClick: () => setIsNavDrawerOpen(false), selected: isActive, sx: {
                                    py: 1.5,
                                    px: 2.5,
                                    color: isActive ? "#fff" : alpha("#fff", 0.75),
                                    borderLeft: `3px solid ${isActive ? "#FF004E" : "transparent"}`,
                                    "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: alpha("#fff", 0.1) },
                                    "&:hover": { backgroundColor: alpha("#fff", 0.08) },
                                } }, { children: [_jsx(ListItemIcon, Object.assign({ sx: { minWidth: 40, color: "inherit", "& .MuiSvgIcon-root": { fontSize: 20 } } }, { children: item.icon })), _jsx(ListItemText, { primary: item.label, primaryTypographyProps: { sx: { fontSize: "15px", fontWeight: isActive ? 600 : 500 } } })] }), item.path));
                        }) }))] }))] })));
};
export default Header;
